import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export interface EtapaDto {
  id: string;
  embudoId: string;
  nombre: string;
  orden: number;
}

export interface EmbudoDto {
  id: string;
  nombre: string;
  tipo: string;
  orden: number;
  etapas: EtapaDto[];
}

export interface UsuarioDto {
  id: string;
  nombre: string;
  correo: string;
  rol: string;
}

export interface CasoDto {
  id: string;
  embudoId: string;
  embudo: string;
  etapaId: string;
  etapa: string;
  contactoId: string;
  contacto: string;
  contactoDocumento?: string | null;
  contactoTelefono?: string | null;
  aseguradora?: string | null;
  titulo: string;
  estado: 'abierto' | 'ganado' | 'perdido';
  semaforo: 'vencido' | 'por_vencer' | 'a_tiempo' | 'sin_fecha' | 'cerrado';
  valor: number;
  fechaVencimiento: string | null;
  origen: string | null;
  responsableId?: string | null;
  responsable?: string | null;
  etiquetas?: string[];
  creadoEn: string;
  etapaDesde: string;
}

export interface ColumnaTablero {
  etapa: EtapaDto;
  casos: CasoDto[];
}

export interface ContactoDto {
  id: string;
  nombres: string;
  apellidos: string | null;
  tipoDocumento: string | null;
  numeroDocumento: string | null;
  telefono: string | null;
  correo: string | null;
  consentimientoDatos: boolean;
  origen: string | null;
  creadoEn: string;
}

export interface ActividadDto {
  id: string;
  casoId: string | null;
  tipo: 'nota' | 'llamada' | 'mensaje' | 'tarea' | 'cambio_etapa' | 'cambio_estado' | 'sistema';
  detalle: string;
  creadoEn: string;
}

export interface FichaContacto {
  contacto: ContactoDto;
  casos: CasoDto[];
  actividad: ActividadDto[];
}

export interface PacienteClinico {
  tipoDocumento: string;
  numeroDocumento: string;
  nombres: string;
  apellidos: string | null;
  telefono: string | null;
  correo: string | null;
  aseguradora: string | null;
  fechaNacimiento: string | null;
}

export interface CitaClinica {
  fechaHora: string;
  sede: string;
  especialidad: string;
  profesional: string;
  estado: 'programada' | 'atendida' | 'cancelada' | 'inasistida';
}

export interface RespuestaCitas {
  citas: CitaClinica[];
  aviso: string | null;
}

export interface NuevoContacto {
  nombres: string;
  apellidos?: string | null;
  tipoDocumento?: string | null;
  numeroDocumento?: string | null;
  telefono?: string | null;
  correo?: string | null;
  consentimientoDatos: boolean;
  origen?: string | null;
}

export interface NuevoCaso {
  embudoId: string;
  etapaId?: string | null;
  contactoId: string;
  titulo: string;
  fechaVencimiento?: string | null;
  origen?: string | null;
  valor: number;
}

@Injectable({ providedIn: 'root' })
export class CrmService {
  private readonly http = inject(HttpClient);

  readonly modalNuevoCaso = signal<{ abierto: boolean; embudoId?: string; etapaId?: string; contactoId?: string }>({ abierto: false });
  readonly modalNuevoPaciente = signal(false);
  readonly versionDatos = signal(0);

  notificarCambio() {
    this.versionDatos.update(v => v + 1);
  }

  obtenerUsuarios() {
    return this.http.get<UsuarioDto[]>('/api/usuarios');
  }

  obtenerEmbudos() {
    return this.http.get<EmbudoDto[]>('/api/embudos');
  }

  obtenerTablero(embudoId: string, estado: string = 'abierto') {
    return this.http.get<ColumnaTablero[]>(`/api/embudos/${embudoId}/tablero?estado=${encodeURIComponent(estado)}`);
  }

  obtenerPendientes(estado: string = 'abierto') {
    return this.http.get<CasoDto[]>(`/api/pendientes?estado=${encodeURIComponent(estado)}`);
  }

  buscarContactos(q?: string) {
    const params = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    return this.http.get<ContactoDto[]>(`/api/contactos${params}`);
  }

  obtenerFichaContacto(id: string) {
    return this.http.get<FichaContacto>(`/api/contactos/${id}`);
  }

  obtenerCitasContacto(id: string) {
    return this.http.get<RespuestaCitas>(`/api/contactos/${id}/citas`);
  }

  consultarPacienteClinico(tipoDocumento: string, numeroDocumento: string) {
    return this.http.get<PacienteClinico>(
      `/api/fuente-clinica/pacientes?tipoDocumento=${encodeURIComponent(tipoDocumento)}&numeroDocumento=${encodeURIComponent(numeroDocumento)}`
    );
  }

  crearContacto(nuevo: NuevoContacto) {
    return this.http.post<{ id: string }>('/api/contactos', nuevo);
  }

  crearCaso(nuevo: NuevoCaso) {
    return this.http.post<{ id: string }>('/api/casos', nuevo);
  }

  cambiarEtapa(casoId: string, etapaId: string) {
    return this.http.patch<void>(`/api/casos/${casoId}/etapa`, { etapaId });
  }

  cambiarEstado(casoId: string, estado: 'abierto' | 'ganado' | 'perdido') {
    return this.http.patch<void>(`/api/casos/${casoId}/estado`, { estado });
  }

  cambiarResponsable(casoId: string, responsableId: string | null) {
    return this.http.patch<void>(`/api/casos/${casoId}/responsable`, { responsableId });
  }

  agregarEtiqueta(casoId: string, etiqueta: string) {
    return this.http.post<void>(`/api/casos/${casoId}/etiquetas`, { etiqueta });
  }

  quitarEtiqueta(casoId: string, etiqueta: string) {
    return this.http.delete<void>(`/api/casos/${casoId}/etiquetas/${encodeURIComponent(etiqueta)}`);
  }

  agregarNota(casoId: string, texto: string) {
    return this.http.post<{ id: string }>(`/api/casos/${casoId}/notas`, { texto });
  }
}
